import React, { useState } from 'react';
import { Grid, Alert, Stepper, Step, StepLabel } from '@mui/material';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import GridContainer from 'components/GridContainer';
import Spacing from 'components/Spacing';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import { useParams } from 'react-router-dom';
import usePartnerTransfersForm from './hooks/usePartnerTransfersForm';
import AddTransfer from './AddTransfer';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useLayoutContext } from 'contexts/LayoutContext';
import TransferActions from './Editor/TransferActions';
import Review from './Review';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import TransferLinkedCapitalActions from './Editor/TransferLinkedCapitalActions';
import api from 'lib/trpcClient';
import PageLoader from 'components/PageLoader';
import TransferGeneralCapitalActions from './Editor/TransferGeneralCapitalActions';
import TransferAdvanceSubscriptions from './Editor/TransferAdvanceSubscriptions';
import TransferFees from './Editor/TransferFees';
import SuccessPage from './SuccessPage';

const sectionLabels: Record<PartnerTransferSection, string> = {
  [PartnerTransferSection.PartnerTransfer]: 'Add partner transfer',
  [PartnerTransferSection.Investments]: 'Investments',
  [PartnerTransferSection.Income]: 'Income',
  [PartnerTransferSection.Expenses]: 'Expenses',
  [PartnerTransferSection.Fees]: 'Fees',
  [PartnerTransferSection.LinkedCapitalActions]: 'Linked capital actions',
  [PartnerTransferSection.GeneralCapitalActions]: 'General capital actions',
  [PartnerTransferSection.AdvanceSubscriptions]: 'Advance subscriptions',
  [PartnerTransferSection.Summary]: 'Review',
};

const AddPartnerTransfer: React.FC = () => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle, isLoading: vehicleLoading } = useVehicle(selectedVehicleId);

  const [sections] = api.partnerTransfers.getSections.useSuspenseQuery(
    { vehicleId: selectedVehicleId ?? '' },
    { staleTime: 0, gcTime: 0 },
  );

  // Load existing partner transfer data if editing
  const { data: existingPartnerTransfer, isLoading: isLoadingPartnerTransfer } =
    api.partnerTransfers.getById.useQuery(
      {
        partnerTransferId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
      },
      {
        enabled: !!id && !!selectedVehicleId,
        staleTime: 0,
        gcTime: 0,
      },
    );

  // Prepare default values from loaded data
  const formDefaultValues =
    !existingPartnerTransfer || !id
      ? undefined
      : {
          partnerTransferId: id,
          trasferrorCommitmentId:
            existingPartnerTransfer.transferorCommitmentId,
          transferDate: existingPartnerTransfer.transferDate,
          transferees: existingPartnerTransfer.newCommitments.transferees.map(
            (transferee) => ({
              investorLabel: transferee.investorName,
              investorId: transferee.investorId,
              amountLcy: transferee.amountLcy,
              percentage: transferee.percentage,
            }),
          ),
        };

  const { formMethods } = usePartnerTransfersForm({
    defaultValues: formDefaultValues,
  });

  const currentPartnerTransferId =
    id || formMethods.getValues('partnerTransferId') || '';

  const pageTitle = currentPartnerTransferId
    ? 'Partner transfers: Edit partner transfer'
    : 'Partner transfers: Create partner transfer';

  const handleNext = (newPartnerTransferId?: string) => {
    if (newPartnerTransferId) {
      const newUrl = `/partner-transfers/${newPartnerTransferId}`;
      window.history.replaceState(null, '', newUrl);
    }
    setActiveStepIndex((prev) => prev + 1);
  };

  const handlePrevious = () => setActiveStepIndex((prev) => prev - 1);

  if (isSuccess) {
    return (
      <PageContainer heading={pageTitle}>
        <SuccessPage />
      </PageContainer>
    );
  }

  const initialState = {
    formMethods,
  };

  if (id && isLoadingPartnerTransfer) {
    return <PageLoader />;
  }

  if (!vehicle) {
    return (
      <PageContainer loading={vehicleLoading} heading={pageTitle}>
        <PageContent>
          <Spacing size="lg">
            <Alert severity="error">Vehicle not found.</Alert>
          </Spacing>
        </PageContent>
      </PageContainer>
    );
  }

  const currentSection = sections[activeStepIndex];

  return (
    <PageContainer loading={vehicleLoading} heading={pageTitle}>
      <PageContent>
        <Spacing size="lg">
          <GridContainer>
            <Grid size={{ sm: 12, md: 12, lg: 8 }}>
              <Stepper activeStep={activeStepIndex} alternativeLabel>
                {sections.map((section) => (
                  <Step key={section}>
                    <StepLabel>{sectionLabels[section]}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          </GridContainer>

          <FormWithStateContextProvider
            formMethods={formMethods}
            initialState={initialState}
          >
            {currentSection === PartnerTransferSection.PartnerTransfer && (
              <AddTransfer
                onNext={handleNext}
                id={id}
                isSubmitting={formMethods.formState.isSubmitting}
                baseCurrency={vehicle.baseCurrency}
                vehicleId={vehicle._id}
              />
            )}
            {currentSection === PartnerTransferSection.Investments && (
              <TransferActions
                partnerTransferSection={PartnerTransferSection.Investments}
                partnerTransferId={currentPartnerTransferId}
                vehicleId={vehicle._id}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            {currentSection === PartnerTransferSection.Income && (
              <TransferActions
                partnerTransferSection={PartnerTransferSection.Income}
                partnerTransferId={currentPartnerTransferId}
                vehicleId={vehicle._id}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            {currentSection === PartnerTransferSection.Expenses && (
              <TransferActions
                partnerTransferSection={PartnerTransferSection.Expenses}
                partnerTransferId={currentPartnerTransferId}
                vehicleId={vehicle._id}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            {currentSection === PartnerTransferSection.Fees && (
              <TransferFees
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            {currentSection === PartnerTransferSection.LinkedCapitalActions && (
              <TransferLinkedCapitalActions
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            {currentSection ===
              PartnerTransferSection.GeneralCapitalActions && (
              <TransferGeneralCapitalActions
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            {currentSection === PartnerTransferSection.AdvanceSubscriptions && (
              <TransferAdvanceSubscriptions
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            {currentSection === PartnerTransferSection.Summary && (
              <Review
                onPrevious={handlePrevious}
                onApprove={() => setIsSuccess(true)}
              />
            )}
          </FormWithStateContextProvider>
        </Spacing>
      </PageContent>
    </PageContainer>
  );
};

export default AddPartnerTransfer;
